import { useState, useEffect } from 'react';
import TopNav from '../components/TopNav';
import Chip from '../components/Chip';
import OrderDetail from './OrderDetail';
import { dashboardApi, walletApi, ordersApi } from '../services/api';
import { useApp } from '../AppContext';

// Mock data (replace with API calls)
// const MOCK_SUMMARY = {
//   completed: 84, inProgress: 23, needInfo: 7, invited: 14,
//   walletBalance: 12450, usedThisMonth: 8320, pendingApprovals: 350, autoRefundPending: 400
// };
// const MOCK_RECENT = [
//   { ref: 'CF-2025-0342', name: 'Amit Sharma', email: 'amit.sharma@abc.com', package: 'Premium', date: '24 May 2025', status: 'Need Info', credits: '₹1,416' },
//   { ref: 'CF-2025-0341', name: 'Priya Nair', email: 'priya.n@xyz.com', package: 'Standard', date: '22 May 2025', status: 'In Progress', credits: '₹708' },
//   { ref: 'CF-2025-0340', name: 'Suresh Menon', email: 'suresh.m@pqr.com', package: 'Std Plus', date: '20 May 2025', status: 'Completed', credits: '₹1,003', hasReport: true },
//   { ref: 'CF-2025-0339', name: 'Kavitha Pillai', email: 'kavitha@lmn.in', package: 'Standard', date: '19 May 2025', status: 'Completed', credits: '₹708', hasReport: true },
//   { ref: 'CF-2025-0338', name: 'Rajan Iyer', email: 'rajan.i@ops.com', package: 'Premium', date: '17 May 2025', status: 'Approval Needed', credits: '₹1,416' },
// ];

function getTodayLabel() {
  const now = new Date();
  const day = now.toLocaleDateString('en-IN', { weekday: 'long' });
  const date = now.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  return `${day}, ${date}`;
}

export default function Dashboard({ onNavigate }) {
  const { addToast, walletBalance, holdBalance, totalBalance, setWalletBalance, setHoldBalance, setTotalBalance, user, minimumBalance } = useApp();
  const [summary, setSummary] = useState({});
  const [attention, setAttention] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loadingApi, setLoadingApi] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [reportDownloading, setReportDownloading] = useState(null);

  async function handleReportDownload(profileNumber) {
    if (reportDownloading === profileNumber) return;
    setReportDownloading(profileNumber);
    try {
      const { blob, filename } = await ordersApi.downloadBGCReport(profileNumber);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      addToast(`Report download failed: ${e.message}`, 'error');
    } finally {
      setReportDownloading(null);
    }
  }

  function refreshAttention() {
    dashboardApi.getClientDashboard().catch(() => null).then(dashRes => {
      if (dashRes?.dsneedinfo) setAttention(Object.values(dashRes.dsneedinfo)[0] || []);
    });
  }

  useEffect(() => {
    setLoadingApi(true);
    Promise.all([
      dashboardApi.getClientDashboard().catch(() => null),
      walletApi.getWalletDetails().catch(() => null),
    ]).then(([dashRes, walletRes]) => {
      if (dashRes?.counts) setSummary(dashRes.counts);
      if (dashRes?.dsordered) setRecent(Object.values(dashRes.dsordered)[0] || []);
      if (dashRes?.dsneedinfo) setAttention(Object.values(dashRes.dsneedinfo)[0] || []);
      if (walletRes) {
        const record = Array.isArray(walletRes) ? walletRes[0] : walletRes;
        if (record) {
          const cur = parseFloat(record.current_balance) || 0;
          const hld = parseFloat(record.hold_balance) || 0;
          setWalletBalance(cur);
          setHoldBalance(hld);
          setTotalBalance(cur + hld);
        }
      }
    }).finally(() => setLoadingApi(false));
  }, []);

  return (
    <>
      <TopNav currentPage="dashboard" onNavigate={onNavigate} />
      <div className="pg">
        <div className="phd">
          <div>
            <div className="ptitle">Hi, {user?.userdescription || user?.username} 👋</div>
            <div className="psub">{getTodayLabel()} · {user?.clientname}</div>
          </div>
          <div className="flex g8">
            <button className="btn bout" onClick={() => onNavigate('new-order-invite')}>📤 Invite Candidate</button>
            <button className="btn bout" onClick={() => onNavigate('new-order-direct')}>⚡ Direct Order</button>
          </div>
        </div>

        {/* Stats */}
        <div className="gr4 mb20">
          <div className="sc" style={{ cursor: 'pointer' }} onClick={() => onNavigate('orders', { statusFilter: 'Completed' })}><div className="si si-gr">✅</div><div><div className="slbl">Completed</div><div className="sval">{summary.closed}</div><div className="snote"></div></div></div>
          <div className="sc" style={{ cursor: 'pointer' }} onClick={() => onNavigate('orders', { statusFilter: 'In-Progress' })}><div className="si si-bl">🔄</div><div><div className="slbl">In Progress</div><div className="sval">{summary.workinprogress}</div><div className="snote"></div></div></div>
          <div className="sc" style={{ cursor: 'pointer' }} onClick={() => onNavigate('orders', { statusFilter: 'Need Information' })}><div className="si si-am">⚠️</div><div><div className="slbl">Need Info</div><div className="sval">{summary.insuff}</div><div className="snote"></div></div></div>
          {/* <div className="sc"><div className="si si-bl">🔄</div><div><div className="slbl">CE Awaited</div><div className="sval">{summary.ceawaited}</div><div className="snote"></div></div></div> */}
          <div className="sc" style={{ cursor: 'pointer' }} onClick={() => onNavigate('orders', { statusFilter: 'InvitedCandidates' })}><div className="si si-or">📨</div><div><div className="slbl">Invited / Pending</div><div className="sval">{summary.totalchecks}</div><div className="snote"></div></div></div>
        </div>

        <div className="gr2 mb20" style={{ alignItems: 'start' , gap : '14px'}}>
          {/* Attention card */}
          <div className="card">
            <div className="chd">
              <div><div className="ctitle">⚠️ Needs Attention</div><div className="csub">Approvals & pending info</div></div>
              <button className="btn bghost bsm" onClick={() => onNavigate('orders')}>View all →</button>
            </div>
            <div style={attention.length > 4 ? { maxHeight: '196px', overflowY: 'auto' } : {}}>
              <table>
                <thead><tr><th>Candidate</th><th>Emp No.</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {attention.length === 0
                    ? <tr><td colSpan={4} style={{ textAlign: 'center', padding: '14px', color: 'var(--g400)', fontSize: '13px' }}>No items need attention</td></tr>
                    : attention.map((a, i) => (
                      <tr key={a.profile_id ?? i}>
                        <td><div className="td1">{a.profilename}</div></td>
                        <td className="tdm">{a.employment_number || '—'}</td>
                        <td><Chip label={a.profilestatus} /></td>
                        <td>
                          <button className="btn bpri bsm" onClick={() => setSelectedOrder({ orderId: a.profile_id, profileStatus: a.profilestatus })}>Review</button>
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          </div>

          {/* Wallet summary */}
          <div className="card">
            <div className="chd">
              <div><div className="ctitle">💳 Wallet Summary</div></div>
              <button className="btn bpri bsm" onClick={() => onNavigate('wallet')}>+ Add Credits</button>
            </div>
            <div className="amtbox">
              <div className="amtrow"><span className="aml">💰 Current Balance</span><span style={{ fontSize: '17px', fontWeight: 800, color: 'var(--or-dk)' }}>₹{walletBalance.toLocaleString('en-IN')}</span></div>
              <div className="amtrow"><span className="aml">🔒 Hold Balance</span><span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--am)' }}>₹{holdBalance.toLocaleString('en-IN')}</span></div>
              <div className="amtrow"><span className="aml">📊 Total Balance</span><span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--bl)' }}>₹{totalBalance.toLocaleString('en-IN')}</span></div>
              {/* {summary.usedthismonth != null && <div className="amtrow"><span className="aml">Used this month</span><span className="amv">₹{summary.usedthismonth?.toLocaleString('en-IN')}</span></div>}
              {summary.autorefundpending != null && <div className="amtrow"><span className="aml">Auto-refund pending</span><span className="amv">₹{summary.autorefundpending?.toLocaleString('en-IN')} (3 candidates)</span></div>} */}
            </div>
            { minimumBalance != 0 && minimumBalance > totalBalance && (
              <div className="alert a-or mt12">
                <div className="aico">💡</div>
                <div className="abody"><div className="atitle">Low credit warning</div>At current pace, balance may run low in ~18 days.</div>
              </div>
            )}
            
          </div>
        </div>

        {/* Recent orders */}
        <div className="card">
          <div className="chd">
            <div><div className="ctitle">📋 Recent Orders</div><div className="csub">Last 30 days</div></div>
            <button className="btn bout bsm" onClick={() => onNavigate('orders')}>View all →</button>
          </div>
          <div className="twrap">
            <table>
              <thead>
                <tr>
                  <th>Ref No.</th>
                  <th>Candidate</th>
                  <th>Package</th>
                  <th>Initiation / Invited Date</th>
                  <th>Status</th>
                  {/* <th>Credits</th> */}
                  <th>Report</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((o, i) => (
                  <tr key={o.profile_number ?? i}>
                    <td className="tdmono">{o.profile_number}</td>
                    <td>
                      <div className="tdl" onClick={() => onNavigate('order-detail', { orderId: o.profile_id, profileStatus: o.profilestatus })}>{o.profilename}</div>
                      <div className="tdm">{o.email}</div>
                    </td>
                    <td><Chip label={o.package_name} /></td>
                    <td className="tdsm">
                      {o.report_initiation_date}
                      {o.candidate_invited_on && (<><br /> <div className="tdm">Invited On [{o.candidate_invited_on}]</div></>)}
                    </td>
                    <td><Chip label={o.profilestatus} /></td>
                    {/* <td className="tdsm">{o.credits}</td> */}
                    <td>{o.hasReport ? <button className="btn bout bsm" onClick={() => handleReportDownload(o.profile_number)} disabled={reportDownloading === o.profile_number}>{reportDownloading === o.profile_number ? <span className="spinner" style={{ width: 11, height: 11 }} /> : '⬇ PDF'}</button> : <span className="tdsm">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedOrder && (
        <OrderDetail
          orderId={selectedOrder.orderId}
          profileStatus={selectedOrder.profileStatus}
          onRefresh={refreshAttention}
          onNavigate={(page) => {
            if (page === 'orders') setSelectedOrder(null);
            else onNavigate(page);
          }}
        />
      )}
    </>
  );
}
